import { useCallback, useState, useEffect } from 'react';
import { AuditEvent } from 'src/models/AuditEvents';
import ServiceCollection from 'src/services/ServiceCollection';

export const useGetAuditEvents = (services: ServiceCollection, registrationNumber: string, pageSize: number) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pagesData, setPagesData] = useState<{ [page: number]: AuditEvent[] }>({});
  const [pageTokens, setPageTokens] = useState<{ [page: number]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  const hasMore = !!pageTokens[currentPage];

  const fetchPageData = useCallback(
    async (page: number) => {
      if (pagesData[page]) return;

      setIsLoading(true);
      try {
        const token = page === 1 ? undefined : pageTokens[page - 1];

        const {
          auditEvents: newEvents,
          nextToken: newToken,
          error,
        } = await services.auditService.getAuditEvents(registrationNumber.replace('*', '.'), pageSize, token);

        if (error) {
          setError(error);
          return;
        }

        setPagesData((prev) => ({ ...prev, [page]: newEvents || [] }));

        if (newToken) {
          setPageTokens((prev) => ({ ...prev, [page]: newToken }));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [registrationNumber, pageSize, pagesData, pageTokens],
  );

  const handlePageChange = useCallback(
    async (newPage: number) => {
      if (newPage === currentPage) return;

      if (pagesData[newPage]) {
        setCurrentPage(newPage);
        return;
      }

      if (newPage > currentPage && !pageTokens[currentPage]) return;

      await fetchPageData(newPage);
      setCurrentPage(newPage);
    },
    [currentPage, pageTokens, pagesData, fetchPageData],
  );

  useEffect(() => {
    setPagesData({});
    setPageTokens({});
    setCurrentPage(1);
  }, [registrationNumber, pageSize]);

  useEffect(() => {
    fetchPageData(currentPage);
  }, [currentPage, fetchPageData]);

  return {
    isGetAuditEventsLoading: isLoading,
    auditEventsList: pagesData[currentPage] || [],
    getAuditEventsError: error,
    currentPage,
    totalPages: hasMore ? currentPage + 1 : currentPage,
    handlePageChange,
  };
};
